import { Accordion, Alert, Box, Button, Checkbox, Group, ScrollArea, Select, Stack, Text, Title } from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconAlertTriangle, IconX } from "@tabler/icons-react";
import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { GeoLayer } from "shared/components/organization/geolocations/types";
import { GEO_QUERY_KEY } from "shared/components/organization/geolocations/useGeoLocations";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useIsMobile } from "shared/hooks/useIsMobile";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

type AssignShapesModalProps = {
    onFetchLayerShapes: (layerId: string) => Promise<void>;
};

export const AssignShapesModal = ({ onFetchLayerShapes }: AssignShapesModalProps) => {
    const isMobile = useIsMobile();

    const { data: layers = [] } = useQuery<GeoLayer[]>({ queryKey: GEO_QUERY_KEY });
    const { data: sendersData } = useSuspenseQuery(queries.senders);

    const senders = sendersData?.senders;

    const [selectedSenderId, setSelectedSenderId] = useState<string | null>(null);
    const [openedLayers, setOpenedLayers] = useState<string[]>([]);

    useEffect(() => {
        if (senders?.length) {
            // select first sender by default
            setSelectedSenderId((current) => current ?? senders[0].id);
        }
    }, [senders]);

    const { data: senderFilters, isLoading: isLoadingFilters } = useQuery(queries.senderFilters(selectedSenderId));

    const [assignedFilterIds, setAssignedFilterIds] = useState<Set<string>>(new Set());
    const [layerSelectionSnapshots, setLayerSelectionSnapshots] = useState<Record<string, string[]>>({});
    const [hoveredLayerId, setHoveredLayerId] = useState<string | null>(null);

    const initialFilterIds = new Set(senderFilters?.map((f) => f.id) ?? []);

    useEffect(() => {
        const ids = new Set(senderFilters?.map((f) => f.id) ?? []);
        setAssignedFilterIds(ids);
        setLayerSelectionSnapshots({});
    }, [senderFilters]);

    const setFilters = useGraphqlMutation(mutations.setFiltersForSender);

    function restoreAndClearSnapshot(layer: GeoLayer, snapshot: string[]) {
        const layerFilters = layer.filters ?? [];
        setAssignedFilterIds((prev) => {
            const next = new Set(prev);
            layerFilters.forEach((filter) => next.delete(filter.id));
            snapshot.forEach((id) => next.add(id));
            return next;
        });
        setLayerSelectionSnapshots((prevSnapshots) => {
            if (!prevSnapshots[layer.id]) return prevSnapshots;
            const nextSnapshots = { ...prevSnapshots };
            delete nextSnapshots[layer.id];
            return nextSnapshots;
        });
    }

    function applyToAllLayerFilters(layer: GeoLayer, remove = false) {
        const layerFilters = layer.filters ?? [];
        const currentSelectionInLayer = layerFilters.filter((filter) => assignedFilterIds.has(filter.id)).map((f) => f.id);
        setLayerSelectionSnapshots((prev) => ({ ...prev, [layer.id]: currentSelectionInLayer }));
        setAssignedFilterIds((prev) => {
            const next = new Set(prev);
            layerFilters.forEach((filter) => (remove ? next.delete(filter.id) : next.add(filter.id)));
            return next;
        });
    }

    function toggleShape(filterId: string, assigned: boolean) {
        setAssignedFilterIds((prev) => {
            const next = new Set(prev);
            assigned ? next.delete(filterId) : next.add(filterId);
            return next;
        });
    }

    function handleSelectAllToggle(layer: GeoLayer, checked: boolean) {
        if (!layer.filters) return;
        if (checked) {
            applyToAllLayerFilters(layer);
        } else {
            const snapshot = layerSelectionSnapshots[layer.id];
            if (snapshot) restoreAndClearSnapshot(layer, snapshot);
        }
    }

    function handleConfirm() {
        if (!selectedSenderId) return;

        setFilters
            .mutateAsync({ senderId: selectedSenderId, filterIds: [...assignedFilterIds] })
            .then(async () => {
                await Promise.allSettled([
                    queryClient.resetQueries({ queryKey: queries.senderFilters(selectedSenderId).queryKey }),
                    queryClient.resetQueries({ queryKey: ["templates"] }),
                    queryClient.resetQueries({ queryKey: ["sender"] }),
                ]);
                notify.show.success("Assignments updated");
                modals.closeAll();
            })
            .catch(notify.catch);
    }

    const isWorking = setFilters.isPending || isLoadingFilters;
    const isDirty =
        !isLoadingFilters &&
        (assignedFilterIds.size !== initialFilterIds.size || [...assignedFilterIds].some((id) => !initialFilterIds.has(id)));
    const openedLayersSet = new Set(openedLayers);

    function handleAccordionChange(values: string[]) {
        const newlyOpened = values.find((value) => !openedLayersSet.has(value));
        if (newlyOpened) void onFetchLayerShapes(newlyOpened);
        setOpenedLayers(values);
    }

    if (!senders?.length) return <Text>No senders available. Create a sender first to assign shapes.</Text>;

    return (
        <Stack gap="md">
            <Select
                label="Sender"
                data={senders.map((sender) => ({ value: sender.id, label: `${sender.name} (${sender.group.name})` }))}
                value={selectedSenderId}
                onChange={(value) => setSelectedSenderId(value ?? null)}
                allowDeselect={false}
            />
            <ScrollArea h="50dvh" type="auto">
                <Group align="center" gap="md">
                    <Title order={3} size="sm" fw={500}>
                        Layers
                    </Title>
                    <Text size="xs">Expand layers to assign shapes</Text>
                </Group>
                {layers.length === 0 ? (
                    <Text size="sm">No layers available.</Text>
                ) : (
                    <Accordion
                        multiple
                        chevronPosition="left"
                        value={openedLayers}
                        onChange={handleAccordionChange}
                        styles={{
                            control: { backgroundColor: "transparent" },
                            panel: { padding: "0.75rem" },
                        }}
                    >
                        {layers.map((layer) => {
                            const filters = layer.filters ?? [];
                            const assignedCount = filters.filter((filter) => assignedFilterIds.has(filter.id)).length;
                            const disabled = !selectedSenderId || isWorking;

                            return (
                                <Accordion.Item key={layer.id} value={layer.id}>
                                    <Box
                                        style={{
                                            display: "flex",
                                            flexDirection: isMobile ? "column" : "row",
                                            alignItems: isMobile ? "stretch" : "center",
                                            paddingRight: "1rem",
                                            backgroundColor:
                                                hoveredLayerId === layer.id ? "var(--mantine-color-default-hover)" : "transparent",
                                            transition: "background-color 150ms ease",
                                        }}
                                        onMouseEnter={() => setHoveredLayerId(layer.id)}
                                        onMouseLeave={() => setHoveredLayerId(null)}
                                    >
                                        <Accordion.Control style={{ flex: 1 }}>
                                            <Text fw={600}>{layer.name}</Text>
                                        </Accordion.Control>
                                    </Box>
                                    <Accordion.Panel>
                                        {filters.length === 0 ? (
                                            <Text size="sm" fs="italic">
                                                {!layer.filters ? "Loading shapes..." : "No shapes yet"}
                                            </Text>
                                        ) : (
                                            <Stack gap="xs">
                                                <Group gap="lg">
                                                    <Checkbox
                                                        label="Select All"
                                                        checked={filters.length > 0 && assignedCount === filters.length}
                                                        indeterminate={assignedCount > 0 && assignedCount < filters.length}
                                                        disabled={disabled}
                                                        fs="italic"
                                                        onChange={(event) => handleSelectAllToggle(layer, event.currentTarget.checked)}
                                                    />
                                                    {assignedCount > 0 && (
                                                        <Group gap="xs" align="center" c="var(--mantine-color-brand-text)">
                                                            <Text size="sm" fs="italic">
                                                                Remove All
                                                            </Text>
                                                            <IconX
                                                                size="16"
                                                                cursor={!disabled ? "pointer" : "not-allowed"}
                                                                onClick={() => !disabled && applyToAllLayerFilters(layer, true)}
                                                            />
                                                        </Group>
                                                    )}
                                                </Group>
                                                {filters.map((filter) => (
                                                    <Group key={filter.id} justify="space-between">
                                                        <Checkbox
                                                            label={filter.name}
                                                            checked={assignedFilterIds.has(filter.id)}
                                                            disabled={disabled}
                                                            onChange={() => toggleShape(filter.id, assignedFilterIds.has(filter.id))}
                                                        />
                                                    </Group>
                                                ))}
                                            </Stack>
                                        )}
                                    </Accordion.Panel>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                )}
            </ScrollArea>
            <Alert color="yellow" icon={<IconAlertTriangle size={20} />} fw="bold">
                If a shape is unassigned, all drafts and templates associated with this sender will be updated to reflect the change.
            </Alert>
            <Group justify="flex-end" mt="md">
                <Button variant="default" onClick={modals.closeAll}>
                    Cancel
                </Button>
                <Button onClick={handleConfirm} loading={isWorking} disabled={!isDirty}>
                    Confirm
                </Button>
            </Group>
        </Stack>
    );
};

type SenderFilter = { id: string; name: string };

async function fetchAllSenderFilters(senderId: string, offset: number, allFilters: SenderFilter[]): Promise<SenderFilter[]> {
    const response = await httpPostGraphql(
        graphql(`
            query GeoLocationsAssignShapesSenderFilters($senderId: UUID!, $offset: Int!) {
                sender(id: $senderId) {
                    filters(limit: 10, offset: $offset) {
                        id
                        name
                    }
                }
            }
        `),
        { senderId, offset },
    );

    const newFilters = response.sender?.filters ?? [];
    const combined = allFilters.concat(newFilters);
    return newFilters.length === 10 ? fetchAllSenderFilters(senderId, offset + 10, combined) : combined;
}

const queries = {
    senders: queryOptions({
        queryKey: ["geolocations", "senders"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query GeoLocationsAssignShapesSenders {
                        senders {
                            id
                            name
                            group {
                                id
                                name
                            }
                        }
                    }
                `),
            ),
    }),
    senderFilters: (senderId: string | null) =>
        queryOptions({
            queryKey: ["geolocations", "senderFilters", senderId],
            queryFn: () => fetchAllSenderFilters(senderId!, 0, []),
            enabled: !!senderId,
        }),
};

const mutations = {
    setFiltersForSender: graphql(`
        mutation GeoLocationsSetFiltersForSender($senderId: UUID!, $filterIds: [UUID!]!) {
            setGeoFiltersForSender(senderId: $senderId, filterIds: $filterIds) {
                id
            }
        }
    `),
};
